import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Building2, Users, TrendingUp, TrendingDown, DollarSign,
  RefreshCw, Loader2, ChevronRight, Clock, UserPlus, Package,
  Activity, AlertTriangle, ArrowUpRight, ArrowDownRight, Zap,
  BarChart3, Target, Users2, UserCheck, Globe, Monitor, Smartphone,
  Tablet, Eye, MousePointer, Timer, Percent, MapPin, Link2, Chrome,
  HeartPulse, CheckCircle2, XCircle, AlertOctagon, X,
  GitBranch, Rocket, PlayCircle, StopCircle, Radio
} from 'lucide-react';
import { toast } from 'sonner';
import { useSafeMode } from '../contexts/SafeModeContext';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '../lib/utils';
import api, { analyticsApi } from '../services/api';
import TokenUtilizationDashboard from '../components/TokenUtilizationDashboard';
import AdminAnalyticsTab from '../components/admin/AdminAnalyticsTab';
import LiveTab from '../components/admin/LiveTab';

// Recharts imports
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  Cell, RadialBarChart, RadialBar
} from 'recharts';

// ==================== CONSTANTS ====================

const TIME_PERIODS = [
  { value: '7', label: '7 Days' },
  { value: '30', label: '30 Days' },
  { value: '90', label: '90 Days' },
  { value: '365', label: '1 Year' },
];

const CHART_COLORS = {
  primary: '#3B82F6',    // Blue
  secondary: '#8B5CF6',  // Purple
  success: '#10B981',    // Green
  warning: '#F59E0B',    // Amber
  danger: '#EF4444',     // Red
  info: '#06B6D4',       // Cyan
  pink: '#EC4899',       // Pink
  indigo: '#6366F1',     // Indigo
};

const DEVICE_COLORS = {
  Desktop: '#3B82F6',
  Mobile: '#10B981',
  Tablet: '#F59E0B',
};

const BROWSER_COLORS = ['#3B82F6', '#8B5CF6', '#10B981', '#F59E0B', '#EF4444', '#EC4899', '#06B6D4', '#6366F1'];

// ==================== STAT CARD COMPONENT ====================

const StatCard = ({ title, value, icon: Icon, color = CHART_COLORS.primary, trend, trendLabel, subtitle, colors }) => (
  <Card className={cn("border", colors.bgCard, colors.borderPrimary)}>
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className={cn("text-xs font-medium uppercase tracking-wide", colors.textMuted)}>{title}</p>
          <p className={cn("text-2xl font-bold mt-1", colors.textPrimary)}>{value}</p>
          {subtitle && <p className={cn("text-xs mt-0.5", colors.textMuted)}>{subtitle}</p>}
          {trend !== undefined && (
            <div className={cn("flex items-center gap-1 mt-2 text-xs", 
              trend >= 0 ? "text-green-500" : "text-red-500"
            )}>
              {trend >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
              <span>{Math.abs(trend)}%</span>
              {trendLabel && <span className={colors.textMuted}>{trendLabel}</span>}
            </div>
          )}
        </div>
        <div 
          className="p-2.5 rounded-lg"
          style={{ backgroundColor: `${color}15` }}
        >
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
      </div>
    </CardContent>
  </Card>
);

// ==================== CUSTOM TOOLTIP ====================

const CustomTooltip = ({ active, payload, label, colors }) => {
  if (!active || !payload || !payload.length) return null;
  
  return (
    <div className={cn("p-3 rounded-lg border shadow-lg", colors?.bgCard || "bg-white", colors?.borderPrimary || "border-gray-200")}>
      <p className={cn("text-sm font-medium mb-1", colors?.textPrimary || "text-gray-900")}>{label}</p>
      {payload.map((entry, index) => (
        <p key={index} className="text-xs" style={{ color: entry.color }}>
          {entry.name}: {typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}
        </p>
      ))}
    </div>
  );
};

// ==================== APP HEALTH TAB ====================

const ErrorDetailRow = ({ label, value, colors }) => (
  <div className="flex items-start gap-3">
    <span className={cn("text-xs font-medium uppercase tracking-wider w-32 shrink-0 pt-0.5", colors.textMuted)}>{label}</span>
    <span className={cn("text-sm break-all", colors.textPrimary)}>{value}</span>
  </div>
);

const AppHealthTab = ({ colors, period }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [selectedError, setSelectedError] = useState(null);
  const [errorsData, setErrorsData] = useState({ errors: [], total: 0, page: 1, total_pages: 1 });
  const [errorsPage, setErrorsPage] = useState(1);
  const [errorsLoading, setErrorsLoading] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await analyticsApi.getHealthMetrics(parseInt(period));
      setData(res.data);
    } catch (err) {
      console.error('Failed to load health metrics:', err);
      setError(err.response?.data?.detail || 'Failed to load health metrics');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadErrors = useCallback(async (pg) => {
    setErrorsLoading(true);
    try {
      const res = await api.get(`/api/admin/metrics/errors?days=${period}&page=${pg}&page_size=25`);
      setErrorsData(res.data);
    } catch { /* ignore */ } finally {
      setErrorsLoading(false);
    }
  }, [period]);

  useEffect(() => { loadErrors(errorsPage); }, [period, errorsPage, loadErrors]);

  const handleClearErrors = async () => {
    if (!confirm('Clear ALL error records? This cannot be undone.')) return;
    try {
      await api.delete('/api/admin/metrics/errors');
      setErrorsData({ errors: [], total: 0, page: 1, total_pages: 1 });
      setErrorsPage(1);
      loadData();
    } catch { /* ignore */ }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className={cn("w-8 h-8 animate-spin", colors.textMuted)} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn("text-center py-20", colors.textMuted)}>
        <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-amber-500" />
        <p className="text-lg font-medium">Failed to load health metrics</p>
        <p className="text-sm mt-1">{error}</p>
        <Button onClick={loadData} className="mt-4">Retry</Button>
      </div>
    );
  }

  const aiHealth = data?.ai_health || {};
  const actionQuality = data?.action_quality || {};
  const systemErrors = data?.system_errors || {};
  const researchQueue = data?.research_queue || {};
  const recentErrors = errorsData?.errors || [];

  // Prepare timeline data for charts
  const aiTimelineData = aiHealth.timeline || [];
  const actionTimelineData = actionQuality.timeline || [];
  const systemTimelineData = systemErrors.timeline || [];

  // Prepare action breakdown data for bar chart
  const actionBreakdownData = Object.entries(actionQuality.by_type || {}).map(([type, stats]) => ({
    name: type.charAt(0).toUpperCase() + type.slice(1),
    accepted: stats.accepted,
    dismissed: stats.dismissed,
    rate: stats.rate
  }));

  return (
    <div className="space-y-6">
      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-4">
        <StatCard
          title="AI Calls"
          value={aiHealth.total_calls?.toLocaleString() || '0'}
          icon={Zap}
          color={CHART_COLORS.primary}
          colors={colors}
        />
        <StatCard
          title="AI Success Rate"
          value={`${aiHealth.success_rate || 100}%`}
          icon={CheckCircle2}
          color={aiHealth.success_rate >= 95 ? CHART_COLORS.success : CHART_COLORS.warning}
          colors={colors}
        />
        <StatCard
          title="503 Errors"
          value={aiHealth.errors_503?.toLocaleString() || '0'}
          icon={AlertOctagon}
          color={aiHealth.errors_503 > 0 ? CHART_COLORS.danger : CHART_COLORS.success}
          subtitle="AI service overload"
          colors={colors}
        />
        <StatCard
          title="Action Accept Rate"
          value={`${actionQuality.acceptance_rate || 0}%`}
          icon={Target}
          color={actionQuality.acceptance_rate >= 70 ? CHART_COLORS.success : CHART_COLORS.warning}
          subtitle={`${actionQuality.accepted || 0} / ${actionQuality.total_actions || 0}`}
          colors={colors}
        />
        <StatCard
          title="System Errors"
          value={(systemErrors.total_4xx + systemErrors.total_5xx || 0).toLocaleString()}
          icon={XCircle}
          color={(systemErrors.total_4xx + systemErrors.total_5xx) > 0 ? CHART_COLORS.danger : CHART_COLORS.success}
          subtitle={`4xx: ${systemErrors.total_4xx || 0} | 5xx: ${systemErrors.total_5xx || 0}`}
          colors={colors}
        />
        <StatCard
          title="Queue Depth"
          value={researchQueue.depth?.toLocaleString() || '0'}
          icon={Activity}
          color={researchQueue.depth > 0 ? CHART_COLORS.warning : CHART_COLORS.success}
          subtitle="Waiting reports"
          colors={colors}
          data-testid="queue-depth-card"
        />
        <StatCard
          title="Queue Processed"
          value={researchQueue.processed?.toLocaleString() || '0'}
          icon={BarChart3}
          color={CHART_COLORS.primary}
          subtitle={`Last ${period}d`}
          colors={colors}
          data-testid="queue-processed-card"
        />
      </div>

      {/* AI Service Health Chart */}
      <Card className={cn("border", colors.bgCard, colors.borderPrimary)}>
        <CardHeader className="pb-2">
          <CardTitle className={cn("text-base font-medium flex items-center gap-2", colors.textPrimary)}>
            <Zap className="w-4 h-4" />
            AI Service Health Over Time
          </CardTitle>
        </CardHeader>
        <CardContent>
          {aiTimelineData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={aiTimelineData}>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.borderPrimary?.replace('border-', '#') || '#e5e7eb'} opacity={0.3} />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 11 }} 
                  tickFormatter={(val) => new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip content={<CustomTooltip colors={colors} />} />
                <Legend />
                <Area type="monotone" dataKey="successes" name="Successes" stroke={CHART_COLORS.success} fill={CHART_COLORS.success} fillOpacity={0.3} />
                <Area type="monotone" dataKey="errors_503" name="503 Errors" stroke={CHART_COLORS.danger} fill={CHART_COLORS.danger} fillOpacity={0.3} />
                <Area type="monotone" dataKey="errors_other" name="Other Errors" stroke={CHART_COLORS.warning} fill={CHART_COLORS.warning} fillOpacity={0.3} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className={cn("text-center py-12", colors.textMuted)}>
              <Zap className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No AI activity data yet</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action Acceptance Over Time - Full Width */}
      <Card className={cn("border", colors.bgCard, colors.borderPrimary)}>
        <CardHeader className="pb-2">
          <CardTitle className={cn("text-base font-medium flex items-center gap-2", colors.textPrimary)}>
            <Target className="w-4 h-4" />
            Action Acceptance Over Time
          </CardTitle>
        </CardHeader>
        <CardContent>
          {actionTimelineData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={actionTimelineData}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 11 }} 
                  tickFormatter={(val) => new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip content={<CustomTooltip colors={colors} />} />
                <Legend />
                <Line type="monotone" dataKey="accepted" name="Accepted" stroke={CHART_COLORS.success} strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="dismissed" name="Dismissed" stroke={CHART_COLORS.danger} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className={cn("text-center py-12", colors.textMuted)}>
              <Target className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No action data yet</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Acceptance by Action Type - Full Width Vertical Bars */}
      <Card className={cn("border", colors.bgCard, colors.borderPrimary)}>
        <CardHeader className="pb-2">
          <CardTitle className={cn("text-base font-medium flex items-center gap-2", colors.textPrimary)}>
            <BarChart3 className="w-4 h-4" />
            Acceptance by Action Type
          </CardTitle>
        </CardHeader>
        <CardContent>
          {actionBreakdownData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={actionBreakdownData}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-30} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip content={<CustomTooltip colors={colors} />} />
                <Legend />
                <Bar dataKey="accepted" name="Accepted" fill={CHART_COLORS.success} />
                <Bar dataKey="dismissed" name="Dismissed" fill={CHART_COLORS.danger} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className={cn("text-center py-12", colors.textMuted)}>
              <BarChart3 className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No action breakdown data yet</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* System Errors Chart */}
      <Card className={cn("border", colors.bgCard, colors.borderPrimary)}>
        <CardHeader className="pb-2">
          <CardTitle className={cn("text-base font-medium flex items-center gap-2", colors.textPrimary)}>
            <AlertTriangle className="w-4 h-4" />
            System Errors Over Time
          </CardTitle>
        </CardHeader>
        <CardContent>
          {systemTimelineData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={systemTimelineData}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 11 }} 
                  tickFormatter={(val) => new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip content={<CustomTooltip colors={colors} />} />
                <Legend />
                <Area type="monotone" dataKey="errors_4xx" name="4xx Errors" stroke={CHART_COLORS.warning} fill={CHART_COLORS.warning} fillOpacity={0.3} />
                <Area type="monotone" dataKey="errors_5xx" name="5xx Errors" stroke={CHART_COLORS.danger} fill={CHART_COLORS.danger} fillOpacity={0.3} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className={cn("text-center py-12", colors.textMuted)}>
              <CheckCircle2 className="w-10 h-10 mx-auto mb-3 text-green-500 opacity-50" />
              <p className="text-sm">No system errors recorded</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Errors Table */}
      <Card className={cn("border", colors.bgCard, colors.borderPrimary)}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className={cn("text-base font-medium flex items-center gap-2", colors.textPrimary)}>
              <AlertOctagon className="w-4 h-4" />
              Errors ({errorsData.total})
            </CardTitle>
            <div className="flex items-center gap-2">
              {errorsData.total > 0 && (
                <button
                  data-testid="clear-errors-btn"
                  onClick={handleClearErrors}
                  className="text-xs text-red-500 hover:text-red-600 font-medium px-2 py-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                >
                  Clear All
                </button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {recentErrors.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className={cn("border-b", colors.borderPrimary)}>
                      <th className={cn("text-left py-2 px-3 font-medium", colors.textMuted)}>Time (CT)</th>
                      <th className={cn("text-left py-2 px-3 font-medium", colors.textMuted)}>Type</th>
                      <th className={cn("text-left py-2 px-3 font-medium", colors.textMuted)}>Context</th>
                      <th className={cn("text-left py-2 px-3 font-medium", colors.textMuted)}>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentErrors.map((err, idx) => (
                      <tr
                        key={idx}
                        data-testid={`error-row-${idx}`}
                        onClick={() => setSelectedError(err)}
                        className={cn("border-b cursor-pointer", colors.borderPrimary, "hover:bg-gray-50 dark:hover:bg-gray-800/50")}
                      >
                        <td className={cn("py-2 px-3 whitespace-nowrap", colors.textSecondary)}>
                          {new Date(err.timestamp).toLocaleString('en-US', { timeZone: 'America/Chicago', month: 'numeric', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true })}
                        </td>
                        <td className="py-2 px-3">
                          <span className={cn(
                            "px-2 py-0.5 rounded text-xs font-medium",
                            err.metric_type === 'ai_error' 
                              ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                              : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                          )}>
                            {err.metric_type === 'ai_error' ? 'AI Error' : 'System Error'}
                          </span>
                        </td>
                        <td className={cn("py-2 px-3", colors.textSecondary)}>
                          {err.context_type || err.details?.context_type || '-'}
                        </td>
                        <td className={cn("py-2 px-3 truncate max-w-xs", colors.textSecondary)}>
                          {err.details?.error_message
                            ? err.details.error_message.substring(0, 100) + (err.details.error_message.length > 100 ? '...' : '')
                            : (err.details?.endpoint || '-')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              {errorsData.total_pages > 1 && (
                <div className="flex items-center justify-between pt-3 px-1">
                  <span className={cn("text-xs", colors.textMuted)}>
                    Page {errorsData.page} of {errorsData.total_pages} ({errorsData.total} total)
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setErrorsPage(p => Math.max(1, p - 1))}
                      disabled={errorsData.page <= 1}
                      className={cn("px-2 py-1 text-xs rounded", errorsData.page <= 1 ? "opacity-40" : "hover:bg-muted", colors.textMuted)}
                    >
                      Prev
                    </button>
                    <button
                      onClick={() => setErrorsPage(p => Math.min(errorsData.total_pages, p + 1))}
                      disabled={errorsData.page >= errorsData.total_pages}
                      className={cn("px-2 py-1 text-xs rounded", errorsData.page >= errorsData.total_pages ? "opacity-40" : "hover:bg-muted", colors.textMuted)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className={cn("text-center py-8", colors.textMuted)}>
              <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-green-500 opacity-50" />
              <p className="text-sm">No errors in the selected time period</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Error Detail Modal */}
      {selectedError && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedError(null)}>
          <div
            data-testid="error-detail-modal"
            onClick={(e) => e.stopPropagation()}
            className={cn("w-full max-w-5xl rounded-lg border shadow-xl max-h-[90vh] flex flex-col", colors.bgCard, colors.borderPrimary)}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b shrink-0">
              <div className="flex items-center gap-3">
                <AlertOctagon className="w-5 h-5 text-red-500" />
                <h3 className={cn("text-lg font-semibold", colors.textPrimary)}>
                  {selectedError.details?.error_class || (selectedError.metric_type === 'ai_error' ? 'AI Error' : 'System Error')}
                </h3>
                <code className={cn("text-xs px-2 py-0.5 rounded font-mono", "bg-gray-100 dark:bg-gray-800", colors.textMuted)}>
                  app_health_metrics/{selectedError.details?.log_id || '-'}
                </code>
                {selectedError.details?.source && (
                  <span className={cn(
                    "text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-medium",
                    selectedError.details.source === 'middleware'
                      ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                      : "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                  )}>
                    {selectedError.details.source}
                  </span>
                )}
              </div>
              <button data-testid="close-error-modal" onClick={() => setSelectedError(null)} className={cn("p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800", colors.textMuted)}>
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-5 overflow-y-auto space-y-5">
              {/* Error message - prominent */}
              {selectedError.details?.error_message && (
                <div className="rounded-lg border border-red-200 dark:border-red-800/50 bg-red-50 dark:bg-red-900/10 p-4">
                  <div className="text-xs font-medium uppercase tracking-wider mb-2 text-red-600 dark:text-red-400">Error Message</div>
                  <pre className="text-sm text-red-800 dark:text-red-300 whitespace-pre-wrap break-all font-mono leading-relaxed">{selectedError.details.error_message}</pre>
                </div>
              )}

              {/* Core fields - 3 column grid */}
              <div className="grid grid-cols-3 gap-x-8 gap-y-3">
                <ErrorDetailRow label="Timestamp (CT)" value={new Date(selectedError.timestamp).toLocaleString('en-US', { timeZone: 'America/Chicago', month: 'numeric', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true })} colors={colors} />
                <ErrorDetailRow label="Status Code" value={selectedError.details?.status_code || '-'} colors={colors} />
                <ErrorDetailRow label="Method" value={selectedError.details?.method || '-'} colors={colors} />
                <ErrorDetailRow label="User" value={selectedError.details?.user_email && selectedError.details.user_email !== 'unknown' ? selectedError.details.user_email : (selectedError.details?.user_id || '-')} colors={colors} />
                <ErrorDetailRow label="Organization" value={selectedError.details?.organization_name || selectedError.details?.organization_id || selectedError.organization_id || '-'} colors={colors} />
                <ErrorDetailRow label="Product" value={selectedError.details?.product_name || selectedError.details?.product_id || selectedError.product_id || '-'} colors={colors} />
                <ErrorDetailRow label="Context" value={selectedError.context_type || selectedError.details?.context_type || '-'} colors={colors} />
              </div>

              {/* AI-specific details for ai_error type */}
              {selectedError.metric_type === 'ai_error' && (
                <>
                  <div className="grid grid-cols-3 gap-x-8 gap-y-3">
                    {selectedError.details?.copilot_type && (
                      <ErrorDetailRow label="Copilot Type" value={selectedError.details.copilot_type} colors={colors} />
                    )}
                    {selectedError.details?.model && (
                      <ErrorDetailRow label="Model" value={selectedError.details.model} colors={colors} />
                    )}
                    {selectedError.details?.error_type && (
                      <ErrorDetailRow label="Error Type" value={selectedError.details.error_type} colors={colors} />
                    )}
                    {selectedError.details?.error_class && (
                      <ErrorDetailRow label="Error Class" value={selectedError.details.error_class} colors={colors} />
                    )}
                    {selectedError.details?.action_type && (
                      <ErrorDetailRow label="Action / Prompt ID" value={selectedError.details.action_type} colors={colors} />
                    )}
                    {selectedError.details?.retry_count != null && (
                      <ErrorDetailRow label="Retry Count" value={selectedError.details.retry_count} colors={colors} />
                    )}
                  </div>

                  {/* User Prompt */}
                  {selectedError.details?.user_prompt && (
                    <div>
                      <div className={cn("text-xs font-medium uppercase tracking-wider mb-1", colors.textMuted)}>User Prompt</div>
                      <pre className={cn("text-sm font-mono whitespace-pre-wrap break-all rounded-lg p-4 max-h-48 overflow-y-auto leading-relaxed", "bg-blue-50 dark:bg-blue-900/10", "text-blue-800 dark:text-blue-300")}>{selectedError.details.user_prompt}</pre>
                    </div>
                  )}

                  {/* System Instruction */}
                  {selectedError.details?.system_instruction && (
                    <div>
                      <div className={cn("text-xs font-medium uppercase tracking-wider mb-1", colors.textMuted)}>System Instruction</div>
                      <pre className={cn("text-sm font-mono whitespace-pre-wrap break-all rounded-lg p-4 max-h-48 overflow-y-auto leading-relaxed", "bg-gray-50 dark:bg-gray-800/50", colors.textPrimary)}>{selectedError.details.system_instruction}</pre>
                    </div>
                  )}

                  {/* Raw AI Response */}
                  {selectedError.details?.raw_ai_response && (
                    <div>
                      <div className="text-xs font-medium uppercase tracking-wider mb-1 text-amber-600 dark:text-amber-400">Raw AI Response</div>
                      <pre className="text-sm font-mono whitespace-pre-wrap break-all rounded-lg p-4 max-h-64 overflow-y-auto leading-relaxed bg-amber-50 dark:bg-amber-900/10 text-amber-800 dark:text-amber-300">{selectedError.details.raw_ai_response}</pre>
                    </div>
                  )}

                  {/* Metadata - resolved names + raw IDs */}
                  {(selectedError.details?.user_email || selectedError.details?.organization_name || selectedError.details?.product_name) && (
                    <div className={cn("rounded-lg p-4", "bg-gray-50 dark:bg-gray-800/30")}>
                      <div className={cn("text-xs font-medium uppercase tracking-wider mb-3", colors.textMuted)}>Metadata</div>
                      <div className="space-y-2">
                        {selectedError.details?.user_email && (
                          <div className="flex gap-4">
                            <span className={cn("text-sm font-medium w-28 shrink-0", colors.textMuted)}>User:</span>
                            <span className={cn("text-sm", colors.textPrimary)}>{selectedError.details.user_email}</span>
                          </div>
                        )}
                        {selectedError.details?.organization_name && (
                          <div className="flex gap-4">
                            <span className={cn("text-sm font-medium w-28 shrink-0", colors.textMuted)}>Organization:</span>
                            <span className={cn("text-sm", colors.textPrimary)}>{selectedError.details.organization_name}</span>
                          </div>
                        )}
                        {selectedError.details?.product_name && (
                          <div className="flex gap-4">
                            <span className={cn("text-sm font-medium w-28 shrink-0", colors.textMuted)}>Product:</span>
                            <span className={cn("text-sm", colors.textPrimary)}>{selectedError.details.product_name}</span>
                          </div>
                        )}
                      </div>
                      {(selectedError.details?.user_id || selectedError.details?.organization_id || selectedError.details?.product_id) && (
                        <details className="mt-3">
                          <summary className={cn("text-xs cursor-pointer", colors.textMuted)}>Raw IDs</summary>
                          <pre className={cn("text-xs font-mono mt-2 p-2 rounded", "bg-gray-100 dark:bg-gray-800", colors.textMuted)}>
{JSON.stringify({
  user_id: selectedError.details?.user_id,
  organization_id: selectedError.details?.organization_id || selectedError.organization_id,
  product_id: selectedError.details?.product_id || selectedError.product_id,
}, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* Non-AI: User Prompt (legacy, shouldn't happen but safe) */}
              {selectedError.metric_type !== 'ai_error' && selectedError.details?.user_prompt && (
                <div>
                  <div className={cn("text-xs font-medium uppercase tracking-wider mb-1", colors.textMuted)}>User Prompt</div>
                  <pre className={cn("text-sm font-mono whitespace-pre-wrap break-all rounded-lg p-4 max-h-48 overflow-y-auto leading-relaxed", "bg-blue-50 dark:bg-blue-900/10", "text-blue-800 dark:text-blue-300")}>{selectedError.details.user_prompt}</pre>
                </div>
              )}

              {/* Endpoint - full width */}
              {selectedError.details?.endpoint && (
                <div>
                  <div className={cn("text-xs font-medium uppercase tracking-wider mb-1", colors.textMuted)}>Endpoint</div>
                  <code className={cn("text-sm font-mono block rounded-lg p-3", "bg-gray-50 dark:bg-gray-800/50", colors.textPrimary)}>{selectedError.details.method} {selectedError.details.endpoint}</code>
                </div>
              )}

              {/* Request body */}
              {selectedError.details?.request_body && (
                <div>
                  <div className={cn("text-xs font-medium uppercase tracking-wider mb-1", colors.textMuted)}>Request Body</div>
                  <pre className={cn("text-sm font-mono whitespace-pre-wrap break-all rounded-lg p-4 max-h-64 overflow-y-auto leading-relaxed", "bg-gray-50 dark:bg-gray-800/50", colors.textPrimary)}>
                    {(() => { try { return JSON.stringify(JSON.parse(selectedError.details.request_body), null, 2); } catch { return selectedError.details.request_body; } })()}
                  </pre>
                </div>
              )}

              {/* Traceback */}
              {selectedError.details?.traceback && (
                <div>
                  <div className="text-xs font-medium uppercase tracking-wider mb-1 text-red-600 dark:text-red-400">Traceback</div>
                  <pre className="text-sm font-mono whitespace-pre-wrap break-all rounded-lg p-4 max-h-80 overflow-y-auto leading-relaxed bg-red-50 dark:bg-red-900/10 text-red-800 dark:text-red-300">
                    {selectedError.details.traceback}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== SITE ANALYTICS TAB ====================

const SiteAnalyticsTab = ({ colors, period }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await analyticsApi.getSiteAnalytics(parseInt(period));
      setData(res.data);
    } catch (err) {
      console.error('Failed to load site analytics:', err);
      setError(err.response?.data?.detail || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className={cn("w-8 h-8 animate-spin", colors.textMuted)} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn("text-center py-20", colors.textMuted)}>
        <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-amber-500" />
        <p className="text-lg font-medium">Failed to load analytics</p>
        <p className="text-sm mt-1">{error}</p>
        <Button onClick={loadData} className="mt-4">Retry</Button>
      </div>
    );
  }

  if (!data) return null;

  const { summary, pageviews_timeline, top_pages, top_referrers, top_countries, device_breakdown, browser_breakdown, os_breakdown, hourly_distribution, screen_breakdown } = data;

  // Format time on page
  const formatTime = (seconds) => {
    if (!seconds || seconds === 0) return '0s';
    const totalSecs = Math.round(seconds);
    const mins = Math.floor(totalSecs / 60);
    const secs = totalSecs % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard
          title="Total Pageviews"
          value={summary.total_pageviews?.toLocaleString() || '0'}
          icon={Eye}
          color={CHART_COLORS.primary}
          colors={colors}
        />
        <StatCard
          title="Unique Visitors"
          value={summary.unique_visitors?.toLocaleString() || '0'}
          icon={Users}
          color={CHART_COLORS.secondary}
          colors={colors}
        />
        <StatCard
          title="Sessions"
          value={summary.unique_sessions?.toLocaleString() || '0'}
          icon={MousePointer}
          color={CHART_COLORS.success}
          colors={colors}
        />
        <StatCard
          title="Bounce Rate"
          value={`${summary.bounce_rate || 0}%`}
          icon={Percent}
          color={summary.bounce_rate > 70 ? CHART_COLORS.danger : CHART_COLORS.warning}
          colors={colors}
        />
        <StatCard
          title="Avg. Time on Page"
          value={formatTime(summary.avg_time_on_page)}
          icon={Timer}
          color={CHART_COLORS.info}
          colors={colors}
        />
        <StatCard
          title="Avg. Scroll Depth"
          value={`${summary.avg_scroll_depth || 0}%`}
          icon={TrendingUp}
          color={CHART_COLORS.pink}
          colors={colors}
        />
      </div>

      {/* Pageviews Timeline */}
      <Card className={cn("border", colors.bgCard, colors.borderPrimary)}>
        <CardHeader className="pb-2">
          <CardTitle className={cn("text-lg flex items-center gap-2", colors.textPrimary)}>
            <BarChart3 className="w-5 h-5" />
            Pageviews Over Time
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pageviews_timeline?.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={pageviews_timeline}>
                <defs>
                  <linearGradient id="colorPageviews" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHART_COLORS.primary} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={CHART_COLORS.primary} stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorVisitors" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHART_COLORS.secondary} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={CHART_COLORS.secondary} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.borderPrimary?.replace('border-', '#') || '#e5e7eb'} opacity={0.5} />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(v) => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  stroke={colors.textMuted?.includes('gray') ? '#9ca3af' : '#6b7280'}
                  fontSize={11}
                />
                <YAxis stroke={colors.textMuted?.includes('gray') ? '#9ca3af' : '#6b7280'} fontSize={11} />
                <Tooltip content={<CustomTooltip colors={colors} />} />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="pageviews" 
                  name="Pageviews"
                  stroke={CHART_COLORS.primary} 
                  fill="url(#colorPageviews)" 
                  strokeWidth={2}
                />
                <Area 
                  type="monotone" 
                  dataKey="visitors" 
                  name="Visitors"
                  stroke={CHART_COLORS.secondary} 
                  fill="url(#colorVisitors)" 
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className={cn("text-center py-12", colors.textMuted)}>
              <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No pageview data available yet</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Two Column Layout: Devices & Browsers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Device Breakdown */}
        <Card className={cn("border", colors.bgCard, colors.borderPrimary)}>
          <CardHeader className="pb-2">
            <CardTitle className={cn("text-lg flex items-center gap-2", colors.textPrimary)}>
              <Monitor className="w-5 h-5" />
              Devices
            </CardTitle>
          </CardHeader>
          <CardContent>
            {device_breakdown?.length > 0 ? (
              <div className="flex items-center">
                <ResponsiveContainer width="50%" height={200}>
                  <PieChart>
                    <Pie
                      data={device_breakdown}
                      dataKey="count"
                      nameKey="device"
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                    >
                      {device_breakdown.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={DEVICE_COLORS[entry.device] || BROWSER_COLORS[index % BROWSER_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-3">
                  {device_breakdown.map((item, index) => (
                    <div key={item.device} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {item.device === 'Desktop' && <Monitor className="w-4 h-4" style={{ color: DEVICE_COLORS.Desktop }} />}
                        {item.device === 'Mobile' && <Smartphone className="w-4 h-4" style={{ color: DEVICE_COLORS.Mobile }} />}
                        {item.device === 'Tablet' && <Tablet className="w-4 h-4" style={{ color: DEVICE_COLORS.Tablet }} />}
                        <span className={cn("text-sm", colors.textPrimary)}>{item.device}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={cn("text-sm font-medium", colors.textPrimary)}>{item.percentage}%</span>
                        <span className={cn("text-xs", colors.textMuted)}>({item.count})</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className={cn("text-center py-8", colors.textMuted)}>
                <Monitor className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No device data yet</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Browser Breakdown */}
        <Card className={cn("border", colors.bgCard, colors.borderPrimary)}>
          <CardHeader className="pb-2">
            <CardTitle className={cn("text-lg flex items-center gap-2", colors.textPrimary)}>
              <Chrome className="w-5 h-5" />
              Browsers
            </CardTitle>
          </CardHeader>
          <CardContent>
            {browser_breakdown?.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={browser_breakdown} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis type="number" fontSize={11} />
                  <YAxis dataKey="browser" type="category" width={80} fontSize={11} />
                  <Tooltip content={<CustomTooltip colors={colors} />} />
                  <Bar dataKey="count" name="Visits" radius={[0, 4, 4, 0]}>
                    {browser_breakdown.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={BROWSER_COLORS[index % BROWSER_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className={cn("text-center py-8", colors.textMuted)}>
                <Chrome className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No browser data yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Geographic & Referrers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Countries */}
        <Card className={cn("border", colors.bgCard, colors.borderPrimary)}>
          <CardHeader className="pb-2">
            <CardTitle className={cn("text-lg flex items-center gap-2", colors.textPrimary)}>
              <Globe className="w-5 h-5" />
              Top Countries
            </CardTitle>
          </CardHeader>
          <CardContent>
            {top_countries?.length > 0 ? (
              <div className="space-y-3">
                {top_countries.slice(0, 8).map((item, index) => {
                  const maxVisits = top_countries[0]?.visits || 1;
                  const percentage = Math.round((item.visits / maxVisits) * 100);
                  return (
                    <div key={item.country} className="space-y-1">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{getCountryFlag(item.code)}</span>
                          <span className={cn("text-sm", colors.textPrimary)}>{item.country}</span>
                        </div>
                        <span className={cn("text-sm font-medium", colors.textPrimary)}>{item.visits.toLocaleString()}</span>
                      </div>
                      <div className={cn("w-full h-1.5 rounded-full", colors.bgTertiary)}>
                        <div 
                          className="h-full rounded-full bg-blue-500"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className={cn("text-center py-8", colors.textMuted)}>
                <Globe className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No geographic data yet</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Referrers */}
        <Card className={cn("border", colors.bgCard, colors.borderPrimary)}>
          <CardHeader className="pb-2">
            <CardTitle className={cn("text-lg flex items-center gap-2", colors.textPrimary)}>
              <Link2 className="w-5 h-5" />
              Top Referrers
            </CardTitle>
          </CardHeader>
          <CardContent>
            {top_referrers?.length > 0 ? (
              <div className="space-y-3">
                {top_referrers.slice(0, 8).map((item, index) => {
                  const maxVisits = top_referrers[0]?.visits || 1;
                  const percentage = Math.round((item.visits / maxVisits) * 100);
                  return (
                    <div key={item.referrer} className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className={cn("text-sm truncate max-w-[200px]", colors.textPrimary)} title={item.referrer}>
                          {item.referrer === '(Direct)' ? '🔗 Direct / Bookmark' : item.referrer}
                        </span>
                        <span className={cn("text-sm font-medium", colors.textPrimary)}>{item.visits.toLocaleString()}</span>
                      </div>
                      <div className={cn("w-full h-1.5 rounded-full", colors.bgTertiary)}>
                        <div 
                          className="h-full rounded-full bg-purple-500"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className={cn("text-center py-8", colors.textMuted)}>
                <Link2 className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No referrer data yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top Pages & Hourly Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Pages */}
        <Card className={cn("border", colors.bgCard, colors.borderPrimary)}>
          <CardHeader className="pb-2">
            <CardTitle className={cn("text-lg flex items-center gap-2", colors.textPrimary)}>
              <Eye className="w-5 h-5" />
              Top Pages
            </CardTitle>
          </CardHeader>
          <CardContent>
            {top_pages?.length > 0 ? (
              <div className="space-y-2">
                {top_pages.slice(0, 8).map((item, index) => (
                  <div key={item.page} className="flex items-center justify-between py-1.5 border-b border-gray-100 dark:border-gray-800 last:border-0">
                    <span className={cn("text-sm truncate max-w-[250px]", colors.textPrimary)} title={item.page}>
                      {item.page}
                    </span>
                    <Badge variant="secondary" className="text-xs">
                      {item.views.toLocaleString()} views
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className={cn("text-center py-8", colors.textMuted)}>
                <Eye className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No page data yet</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Hourly Distribution */}
        <Card className={cn("border", colors.bgCard, colors.borderPrimary)}>
          <CardHeader className="pb-2">
            <CardTitle className={cn("text-lg flex items-center gap-2", colors.textPrimary)}>
              <Clock className="w-5 h-5" />
              Traffic by Hour (UTC)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {hourly_distribution?.some(h => h.visits > 0) ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={hourly_distribution}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis 
                    dataKey="hour" 
                    tickFormatter={(h) => `${h}:00`}
                    fontSize={10}
                    interval={2}
                  />
                  <YAxis fontSize={11} />
                  <Tooltip 
                    content={<CustomTooltip colors={colors} />}
                    labelFormatter={(h) => `${h}:00 - ${h}:59 UTC`}
                  />
                  <Bar dataKey="visits" name="Visits" fill={CHART_COLORS.info} radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className={cn("text-center py-8", colors.textMuted)}>
                <Clock className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No hourly data yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Helper function for country flags
const getCountryFlag = (code) => {
  if (!code || code === 'XX') return '🌍';
  const codePoints = code
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

// ==================== APP ANALYTICS TAB ====================

const AppAnalyticsTab = ({ colors, period }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await analyticsApi.getAppAnalytics(parseInt(period));
      setData(res.data);
    } catch (err) {
      console.error('Failed to load app analytics:', err);
      setError(err.response?.data?.detail || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className={cn("w-8 h-8 animate-spin", colors.textMuted)} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn("text-center py-20", colors.textMuted)}>
        <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-amber-500" />
        <p className="text-lg font-medium">Failed to load analytics</p>
        <p className="text-sm mt-1">{error}</p>
        <Button onClick={loadData} className="mt-4">Retry</Button>
      </div>
    );
  }

  if (!data) return null;

  const { summary, pageviews_timeline, top_pages, device_breakdown, browser_breakdown } = data;

  const formatTime = (seconds) => {
    if (!seconds || seconds === 0) return '0s';
    const totalSecs = Math.round(seconds);
    const mins = Math.floor(totalSecs / 60);
    const secs = totalSecs % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard
          title="Total Pageviews"
          value={summary.total_pageviews?.toLocaleString() || '0'}
          icon={Eye}
          color={CHART_COLORS.primary}
          colors={colors}
        />
        <StatCard
          title="Active Users"
          value={summary.unique_visitors?.toLocaleString() || '0'}
          icon={Users}
          color={CHART_COLORS.secondary}
          colors={colors}
        />
        <StatCard
          title="Sessions"
          value={summary.unique_sessions?.toLocaleString() || '0'}
          icon={MousePointer}
          color={CHART_COLORS.success}
          colors={colors}
        />
        <StatCard
          title="Bounce Rate"
          value={`${summary.bounce_rate || 0}%`}
          icon={Percent}
          color={summary.bounce_rate > 70 ? CHART_COLORS.danger : CHART_COLORS.warning}
          colors={colors}
        />
        <StatCard
          title="Avg. Time on Page"
          value={formatTime(summary.avg_time_on_page)}
          icon={Timer}
          color={CHART_COLORS.info}
          colors={colors}
        />
        <StatCard
          title="Avg. Scroll Depth"
          value={`${summary.avg_scroll_depth || 0}%`}
          icon={TrendingUp}
          color={CHART_COLORS.pink}
          colors={colors}
        />
      </div>

      {/* Activity Timeline */}
      <Card className={cn("border", colors.bgCard, colors.borderPrimary)}>
        <CardHeader className="pb-2">
          <CardTitle className={cn("text-lg flex items-center gap-2", colors.textPrimary)}>
            <Activity className="w-5 h-5" />
            App Activity Over Time
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pageviews_timeline?.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={pageviews_timeline}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(v) => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  fontSize={11}
                />
                <YAxis fontSize={11} />
                <Tooltip content={<CustomTooltip colors={colors} />} />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="pageviews" 
                  name="Page Views"
                  stroke={CHART_COLORS.primary} 
                  strokeWidth={2}
                  dot={{ fill: CHART_COLORS.primary, r: 3 }}
                  activeDot={{ r: 5 }}
                />
                <Line 
                  type="monotone" 
                  dataKey="visitors" 
                  name="Active Users"
                  stroke={CHART_COLORS.success} 
                  strokeWidth={2}
                  dot={{ fill: CHART_COLORS.success, r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className={cn("text-center py-12", colors.textMuted)}>
              <Activity className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No activity data available yet</p>
              <p className="text-sm mt-1">App usage will appear here once users start using the application</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top Pages in App */}
      <Card className={cn("border", colors.bgCard, colors.borderPrimary)}>
        <CardHeader className="pb-2">
          <CardTitle className={cn("text-lg flex items-center gap-2", colors.textPrimary)}>
            <Eye className="w-5 h-5" />
            Most Visited Pages
          </CardTitle>
        </CardHeader>
        <CardContent>
          {top_pages?.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={top_pages.slice(0, 10)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis type="number" fontSize={11} />
                <YAxis 
                  dataKey="page" 
                  type="category" 
                  width={150} 
                  fontSize={11}
                  tickFormatter={(v) => v.length > 25 ? v.slice(0, 25) + '...' : v}
                />
                <Tooltip content={<CustomTooltip colors={colors} />} />
                <Bar dataKey="views" name="Views" fill={CHART_COLORS.secondary} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className={cn("text-center py-8", colors.textMuted)}>
              <Eye className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No page data yet</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== BUSINESS METRICS TAB ====================

const BusinessMetricsTab = ({ colors, period }) => {
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [health, setHealth] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const periodDays = parseInt(period) || 30;
      const [metricsRes, healthRes] = await Promise.all([
        api.get(`/api/admin/metrics/dashboard?period=${periodDays}d`),
        api.get('/api/admin/metrics/health'),
      ]);
      setMetrics(metricsRes.data);
      setHealth(healthRes.data);
    } catch (err) {
      console.error('Failed to load business metrics:', err);
      toast.error('Failed to load metrics');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className={cn("w-8 h-8 animate-spin", colors.textMuted)} />
      </div>
    );
  }

  const periodLabel = `${period}d`;

  return (
    <div className="space-y-6">
      {/* Key Business Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="Total Organizations"
          value={metrics?.state?.total_orgs || 0}
          icon={Building2}
          color={CHART_COLORS.primary}
          colors={colors}
          subtitle={`+${metrics?.flow?.new_signups || 0} new (${periodLabel})`}
        />
        <StatCard
          title="Total Users"
          value={metrics?.state?.total_users || 0}
          icon={Users}
          color={CHART_COLORS.secondary}
          colors={colors}
          subtitle={`+${metrics?.flow?.new_users || 0} new (${periodLabel})`}
        />
        <StatCard
          title="Paying Customers"
          value={metrics?.state?.paid_orgs || 0}
          icon={DollarSign}
          color={CHART_COLORS.success}
          colors={colors}
        />
        <StatCard
          title="Monthly Revenue"
          value={`$${(metrics?.state?.mrr || 0).toLocaleString()}`}
          icon={TrendingUp}
          color={CHART_COLORS.warning}
          colors={colors}
        />
      </div>

      {/* Conversion Funnel */}
      <Card className={cn("border", colors.bgCard, colors.borderPrimary)}>
        <CardHeader className="pb-2">
          <CardTitle className={cn("text-lg flex items-center gap-2", colors.textPrimary)}>
            <Target className="w-5 h-5" />
            Conversion Funnel ({periodLabel})
          </CardTitle>
          {(metrics?.activation?.unique_visitors > 0 || metrics?.activation?.signups > 0) && (
            <p className={cn("text-sm mt-1", colors.textMuted)}>
              <span className={cn("font-semibold", colors.textPrimary)}>
                {(metrics?.activation?.unique_visitors || 0).toLocaleString()}
              </span>
              {' '}unique visitors{' '}
              <ChevronRight className="inline w-3 h-3" />
              {' '}
              <span className={cn("font-semibold", colors.textPrimary)}>
                {(metrics?.activation?.signups || 0).toLocaleString()}
              </span>
              {' '}sign ups{' '}
              <span className="text-xs">
                ({metrics?.activation?.visit_to_signup_rate || 0}% conversion)
              </span>
            </p>
          )}
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[
              { label: 'Sign Up', value: metrics?.activation?.signups || 0, color: '#8b5cf6' },
              { label: 'Create Product', value: metrics?.activation?.created_product || 0, color: '#a78bfa' },
              { label: 'Integrated', value: metrics?.activation?.integrated || 0, color: '#3b82f6' },
              { label: 'First Release Created', value: metrics?.activation?.first_release || 0, color: '#06b6d4' },
              { label: 'First Sprint Start', value: metrics?.activation?.first_sprint_start || 0, color: '#10b981' },
              { label: 'First Sprint Close', value: metrics?.activation?.first_sprint_close || 0, color: '#f59e0b' },
              { label: 'First Promote to Prod', value: metrics?.activation?.first_promote || 0, color: '#ef4444' },
            ].map((stage, index, arr) => {
              const maxValue = arr[0]?.value || 1;
              const percentage = maxValue > 0 ? Math.round((stage.value / maxValue) * 100) : 0;
              return (
                <div key={stage.label} className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className={cn("text-sm font-medium", colors.textPrimary)}>{stage.label}</span>
                    <div className="flex items-center gap-2">
                      <span className={cn("text-sm font-bold", colors.textPrimary)}>{stage.value.toLocaleString()}</span>
                      <span className={cn("text-xs", colors.textMuted)}>({percentage}%)</span>
                    </div>
                  </div>
                  <div className={cn("w-full h-2.5 rounded-full", colors.bgTertiary)}>
                    <div 
                      className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${Math.max(percentage, 1)}%`, backgroundColor: stage.color }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Time to First Milestones */}
      <Card className={cn("border", colors.bgCard, colors.borderPrimary)}>
        <CardHeader className="pb-2">
          <CardTitle className={cn("text-lg flex items-center gap-2", colors.textPrimary)}>
            <Clock className="w-5 h-5" />
            Time to First Milestones
          </CardTitle>
          <p className={cn("text-xs mt-1", colors.textMuted)}>Median time from signup to each milestone</p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { key: 'product', label: 'First Product', icon: Package, color: '#a78bfa' },
              { key: 'integration', label: 'First Integration', icon: GitBranch, color: '#3b82f6' },
              { key: 'release', label: 'First Release', icon: Rocket, color: '#06b6d4' },
              { key: 'sprint_start', label: 'First Sprint Start', icon: PlayCircle, color: '#10b981' },
              { key: 'sprint_close', label: 'First Sprint Close', icon: StopCircle, color: '#f59e0b' },
              { key: 'promote', label: 'First Promote', icon: CheckCircle2, color: '#ef4444' },
            ].map(({ key, label, icon: Icon, color }) => {
              const ttf = metrics?.time_to_first?.[key];
              const values = ttf?.values || [];
              const sparkMax = Math.max(...values, 1);
              return (
                <div key={key} className={cn("p-4 rounded-lg", colors.bgTertiary)}>
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className="w-4 h-4" style={{ color }} />
                    <p className={cn("text-xs font-medium", colors.textMuted)}>{label}</p>
                  </div>
                  <p className={cn("text-xl font-bold", colors.textPrimary)}>
                    {ttf?.median_display || 'N/A'}
                  </p>
                  <p className={cn("text-xs mt-0.5", colors.textMuted)}>
                    {ttf?.sample_size || 0} org{(ttf?.sample_size || 0) !== 1 ? 's' : ''}
                  </p>
                  {values.length > 1 && (
                    <div className="flex items-end gap-px mt-3 h-8">
                      {values.slice(-20).map((v, i) => (
                        <div
                          key={i}
                          className="flex-1 rounded-t-sm min-w-[3px] transition-all"
                          style={{
                            height: `${Math.max((v / sparkMax) * 100, 8)}%`,
                            backgroundColor: color,
                            opacity: 0.4 + (i / Math.max(values.slice(-20).length - 1, 1)) * 0.6,
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Health Alerts */}
      {health && (
        <Card className={cn("border", colors.bgCard, colors.borderPrimary)}>
          <CardHeader className="pb-2">
            <CardTitle className={cn("text-lg flex items-center gap-2", colors.textPrimary)}>
              <AlertTriangle className="w-5 h-5" />
              System Health
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className={cn("p-3 rounded-lg", colors.bgTertiary)}>
                <p className={cn("text-xs", colors.textMuted)}>Trials Expiring Soon</p>
                <p className={cn("text-2xl font-bold", health.trial_health?.expiring_soon > 0 ? "text-amber-500" : colors.textPrimary)}>
                  {health.trial_health?.expiring_soon || 0}
                </p>
              </div>
              <div className={cn("p-3 rounded-lg", colors.bgTertiary)}>
                <p className={cn("text-xs", colors.textMuted)}>Past Due Subscriptions</p>
                <p className={cn("text-2xl font-bold", health.subscription_health?.past_due > 0 ? "text-red-500" : colors.textPrimary)}>
                  {health.subscription_health?.past_due || 0}
                </p>
              </div>
              <div className={cn("p-3 rounded-lg", colors.bgTertiary)}>
                <p className={cn("text-xs", colors.textMuted)}>Inactive Orgs (30d)</p>
                <p className={cn("text-2xl font-bold", colors.textPrimary)}>
                  {health.engagement_health?.inactive_30d || 0}
                </p>
              </div>
              <div className={cn("p-3 rounded-lg", colors.bgTertiary)}>
                <p className={cn("text-xs", colors.textMuted)}>Empty Products</p>
                <p className={cn("text-2xl font-bold", colors.textPrimary)}>
                  {health.content_health?.empty_products || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ==================== MAIN ADMIN PAGE ====================

const AdminPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getThemeColors } = useSafeMode();
  const { user } = useAuth();
  const colors = getThemeColors();
  
  const [period, setPeriod] = useState('30');
  const [activeTab, setActiveTab] = useState('live');

  // Check admin access
  useEffect(() => {
    if (user && user.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  if (!user || user.role !== 'admin') {
    return (
      <div className={cn("flex h-screen", colors.bgPrimary)}>
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className={cn("w-8 h-8 animate-spin", colors.textMuted)} />
        </div>
      </div>
    );
  }

  return (
    <div className={cn("flex h-screen", colors.bgPrimary)}>
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />
        <main className="flex-1 p-6 overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className={cn("text-2xl font-bold", colors.textPrimary)}>Analytics Dashboard</h1>
              <p className={cn("text-sm", colors.textMuted)}>Monitor site traffic, app usage, and business metrics</p>
            </div>
            <div className="flex items-center gap-3">
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className={cn("w-32", colors.bgInput, colors.borderPrimary)}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                  {TIME_PERIODS.map(p => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className={cn("grid grid-cols-7 w-full max-w-4xl", colors.bgSecondary)}>
              <TabsTrigger value="live" className="flex items-center gap-2" data-testid="live-tab-trigger">
                <Radio className="w-4 h-4" />
                Live
              </TabsTrigger>
              <TabsTrigger value="health" className="flex items-center gap-2">
                <HeartPulse className="w-4 h-4" />
                App Health
              </TabsTrigger>
              <TabsTrigger value="site" className="flex items-center gap-2">
                <Globe className="w-4 h-4" />
                Site Traffic
              </TabsTrigger>
              <TabsTrigger value="business" className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                Business
              </TabsTrigger>
              <TabsTrigger value="analytics" className="flex items-center gap-2">
                <Target className="w-4 h-4" />
                Analytics
              </TabsTrigger>
              <TabsTrigger value="app" className="flex items-center gap-2">
                <Activity className="w-4 h-4" />
                App Usage
              </TabsTrigger>
              <TabsTrigger value="tokens" className="flex items-center gap-2">
                <Zap className="w-4 h-4" />
                Tokens
              </TabsTrigger>
            </TabsList>

            <TabsContent value="live">
              <LiveTab colors={colors} />
            </TabsContent>

            <TabsContent value="health">
              <AppHealthTab colors={colors} period={period} />
            </TabsContent>

            <TabsContent value="analytics">
              <AdminAnalyticsTab period={period} />
            </TabsContent>

            <TabsContent value="site">
              <SiteAnalyticsTab colors={colors} period={period} />
            </TabsContent>

            <TabsContent value="app">
              <AppAnalyticsTab colors={colors} period={period} />
            </TabsContent>

            <TabsContent value="business">
              <BusinessMetricsTab colors={colors} period={period} />
            </TabsContent>

            <TabsContent value="tokens">
              <TokenUtilizationDashboard />
            </TabsContent>
          </Tabs>
        </main>
      </div>
    </div>
  );
};

export default AdminPage;
